import { NotificationTemplateCopy, TemplateProblem } from '../../types/notificationTemplate';

/*
 * The same rules madkhol-notifier applies when a template is saved (`src/helpers/notification/template-rules.ts`), run
 * here so the form can explain a problem before the round trip. The server remains the authority: keep the two in step.
 */

/** Lowercase dot-separated segments, e.g. `funds.card_payment_confirmed`. */
export const KEY_PATTERN = /^[a-z][a-z0-9_]*(\.[a-z0-9_]+)+$/;
export const KEY_MAX_LENGTH = 100;

/** A SendGrid dynamic template id: `d-` and 32 hex characters. */
export const SENDGRID_TEMPLATE_ID_PATTERN = /^d-[0-9a-f]{32}$/i;

export const isValidKey = (key: string) => KEY_PATTERN.test(key) && key.length <= KEY_MAX_LENGTH;

const PLACEHOLDER = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;

/** Every `{{name}}` in the text, by name, in order of first use. */
export const extractPlaceholders = (text?: string | null): string[] => [
  ...new Set([...(text ?? '').matchAll(PLACEHOLDER)].map((match) => match[1])),
];

/** `{{amountAr}}` and `{{amountEn}}` read the same `amount` param, in the language's own digits. */
const paramRoot = (name: string) => name.replace(/(En|Ar)$/, '');

export function validateTemplateCopy(copy: NotificationTemplateCopy): TemplateProblem[] {
  const problems: TemplateProblem[] = [];
  const sides = [
    { language: 'ar' as const, title: copy.titleAr, body: copy.bodyAr },
    { language: 'en' as const, title: copy.titleEn, body: copy.bodyEn },
  ];

  sides.forEach(({ language, title, body }) => {
    if (!body?.trim()) {
      problems.push({ code: 'EMPTY_BODY', language });
    }

    if ([title, body].some((text) => /\{\{|\}\}/.test((text ?? '').replace(PLACEHOLDER, '')))) {
      problems.push({ code: 'MALFORMED_PLACEHOLDER', language });
    }
  });

  const [arabic, english] = sides.map(({ title, body }) => [
    ...new Set([...extractPlaceholders(title), ...extractPlaceholders(body)]),
  ]);
  const arabicRoots = new Set(arabic.map(paramRoot));
  const englishRoots = new Set(english.map(paramRoot));
  const onlyInArabic = arabic.filter((name) => !englishRoots.has(paramRoot(name))).sort();
  const onlyInEnglish = english.filter((name) => !arabicRoots.has(paramRoot(name))).sort();

  if (onlyInArabic.length || onlyInEnglish.length) {
    problems.push({ code: 'PLACEHOLDER_MISMATCH', onlyInArabic, onlyInEnglish });
  }

  return problems;
}

const LANGUAGE_NAMES = { ar: 'Arabic', en: 'English' };

export function describeProblem(problem: TemplateProblem): string {
  switch (problem.code) {
    case 'EMPTY_BODY':
      return `The ${LANGUAGE_NAMES[problem.language]} body is empty.`;
    case 'MALFORMED_PLACEHOLDER':
      return `The ${
        LANGUAGE_NAMES[problem.language]
      } text has a broken placeholder. Write placeholders as {{name}}, using letters, digits and underscores only.`;
    case 'PLACEHOLDER_MISMATCH':
      return [
        'Both languages must use the same placeholders, because one set of values fills both.',
        problem.onlyInArabic.length ? `Only in Arabic: ${problem.onlyInArabic.map((p) => `{{${p}}}`).join(', ')}.` : '',
        problem.onlyInEnglish.length
          ? `Only in English: ${problem.onlyInEnglish.map((p) => `{{${p}}}`).join(', ')}.`
          : '',
      ]
        .filter(Boolean)
        .join(' ');
    case 'INVALID_KEY':
      return 'The key must be lowercase words separated by dots, e.g. funds.card_payment_confirmed.';
    case 'INVALID_SENDGRID_TEMPLATE_ID':
      return 'The SendGrid template id must be d- followed by 32 letters and digits, as SendGrid shows it.';
    case 'UNKNOWN_CATEGORY':
      return `${problem.category} is not a category.`;
    default:
      return 'The template was refused.';
  }
}

/** What renaming a key does: code sends a fixed key, so the old one stops finding this template. */
export const describeKeyRename = (from: string, to: string) =>
  `After this, code that sends "${from}" finds no template and sends nothing, including notifications already ` +
  `scheduled. Rename only once the code sends "${to}".`;

/** Reads the error the gateway passes through from madkhol-notifier. */
export function describeSaveError(error: any, fallback: string): string[] {
  const response = error?.data;

  if (response?.message === 'INVALID_TEMPLATE' && Array.isArray(response?.data?.problems)) {
    return (response.data.problems as TemplateProblem[]).map(describeProblem);
  }

  if (response?.message === 'TEMPLATE_EXISTS') {
    return ['This key already has a template. Choose another key, or edit that template instead.'];
  }

  return [response?.message || fallback];
}

/** Placeholders the edited copy uses that the saved copy does not: the producer may not send a value for them. */
export function newPlaceholders(saved: NotificationTemplateCopy, edited: NotificationTemplateCopy): string[] {
  const all = (copy: NotificationTemplateCopy) =>
    new Set(Object.values(copy).flatMap((text) => extractPlaceholders(text)));
  const before = all(saved);

  return [...all(edited)].filter((name) => !before.has(name));
}
